import type { PushSupport } from './web-push-client';

/**
 * When the installed app should nudge "turn on notifications". Pure, so every
 * combination is unit-tested; the banner component only gathers the inputs.
 *
 *   - `ask`     never switched on here (or permission granted elsewhere, e.g.
 *               an old prompt, but push itself never turned on): offer a
 *               button - the browser's own prompt needs a tap to appear.
 *   - `blocked` the user said "Block" / "Don't Allow" once: no web page may
 *               ask again, so show where to allow it in the phone's settings.
 *   - `hidden`  everything else - a browser tab (the install banner covers
 *               that), no push here at all, server switch off, snoozed, or
 *               already on.
 */
export type PushBannerState = 'hidden' | 'ask' | 'blocked';

export interface PushBannerInputs {
  /** Running as the installed app (home-screen icon / desktop app window). */
  standalone: boolean;
  support: PushSupport;
  /** Server has push switched on and handed us a key. */
  serverReady: boolean;
  permission: NotificationPermission | 'unsupported';
  /** This user turned push on for this device (the app-open sync keeps it live). */
  optedInHere: boolean;
  snoozed: boolean;
}

export const pushBannerState = (i: PushBannerInputs): PushBannerState => {
  if (!i.standalone || i.support !== 'supported' || !i.serverReady || i.snoozed) return 'hidden';
  if (i.permission === 'unsupported') return 'hidden';
  if (i.permission === 'denied') return 'blocked';
  if (i.optedInHere && i.permission === 'granted') return 'hidden';
  return 'ask';
};

const SNOOZE_KEY = 'utazzo.push.bannerSnoozedUntil';

export const DAY_MS = 24 * 60 * 60 * 1000;

/** "Later" on the banner, or a deliberate "Turn off here": stay quiet for a while. */
export const snoozePushBanner = (forMs: number, now: number = Date.now()): void => {
  try {
    localStorage.setItem(SNOOZE_KEY, String(now + forMs));
  } catch {
    // Private mode / storage blocked - the banner just shows again next open.
  }
};

export const isPushBannerSnoozed = (now: number = Date.now()): boolean => {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
};
